import { defineCommand, defineReceiver } from '@drapejs/invoker';
import { fetchPage } from '@drapejs/core';
import { batch, request, query } from '@motillo/drapejs-litium';
import { gql } from 'graphql-request';

const takeProductsCount = 24;

export const commands = {
  loadAllSectionProducts: defineCommand<{
    categorySystemId: string;
    section: any;
    path: string;
    cacheKey: string;
  }>('loadAllSectionProducts')
};

function isGiftCategory(page: any) {
  return (
    page?.dataJson?.dataJson?.category?.template === 'Gifts' ||
    page?.dataJson?.dataJson?.category?.template === 'GetMyGiftCategory'
  );
}

function isTestCategory(page: any) {
  return page?.dataJson?.dataJson?.category?.fields?.TestAssortment == true;
}

export const schedule = defineReceiver(
  fetchPage,
  async function (command, data) {
    if (!isGiftCategory(data)) {
      return data;
    }
    const activeGiftCard = await this.cache.getItem('__activeGiftCard');
    let categoryId = activeGiftCard?.firstCategory?.categoryId;

    if (!categoryId && isTestCategory(data)) {
      categoryId = data?.dataJson?.dataJson?.category?.systemId;
    }

    if (!data?.dataJson?.dataJson?.product) {
      await this.cache.setItem?.(
        '__categoryId',
        data?.dataJson?.dataJson?.category?.systemId || ''
      );
      await this.cache.setItem?.('__categoryUrl', command.path || '');
    }

    if (!categoryId) {
      return data;
    }

    const cacheKey = getProductSectionsCacheKey(command.path, categoryId);
    const categoryResult = await this.cache.getItem(cacheKey);

    const sections = (categoryResult?.categoryGiftsV2?.productSections || [])
      .filter((e: any) => e.take > takeProductsCount)
      .map((e: any) => {
        return {
          take: e.take,
          tags: (e.tags || []).map((t: any) => {
            return {
              key: t.key,
              value: t.value
            };
          })
        };
      });

    await this.invoke(batch.scheduleQuery, {
      cacheKey: cacheKey,
      query: gql`
      search {
        categoryGiftsV2(categorySystemId: $categorySystemId, take: $take, sections: $sections){
          productSections {
            ${getProductSectionQueryFields()}
          },
          singleSection        
        }
      }`,
      variables: {
        categorySystemId: {
          value: categoryId,
          type: 'String!'
        },
        take: {
          value: takeProductsCount,
          type: 'Int'
        },
        sections: {
          value: sections,
          type: '[ProductSection]'
        }
      }
    });

    return data;
  }
);

export const receivers = {
  loadAllSectionProducts: defineReceiver(
    commands.loadAllSectionProducts,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            query loadAllSectionProducts($url: String!, $categorySystemId: String!, $section: ProductSection) {
              session(url: $url) {
                search {
                  sectionCategoryGifts(
                    categorySystemId: $categorySystemId,
                    section: $section
                  ) {
                    ${getProductSectionQueryFields()}
                  }
                }
              }
            }
          `
        ),
        {
          url: `${(<any>command).protocol}//${(<any>command).host}${
            (<any>command).path
          }`,
          categorySystemId: command.categorySystemId,
          section: command.section
        }
      );

      const cacheKey = getProductSectionsCacheKey(
        command.path,
        command.categorySystemId
      );
      let categoryResult = await this.cache.getItem(cacheKey);

      const productSection = result.data.session.search.sectionCategoryGifts;
      const tag = productSection?.tags?.[0];
      (categoryResult?.categoryGiftsV2?.productSections || []).forEach(
        (ps: any, i: any) => {
          const t = ps.tags?.[0];
          if (tag?.key && t?.value === tag?.value) {
            categoryResult.categoryGiftsV2.productSections[i] = productSection;
          }
        }
      );

      await this.cache.setItem(cacheKey, categoryResult);
      return categoryResult;
    }
  )
};

export function getProductSectionsCacheKey(path: any, categorySystemId: any) {
  const channelRoot =
    path.split('/').filter((s: any) => s.replace('/', ''))?.[0] || '';
  return `products:${channelRoot}:${categorySystemId}`;
}

function getProductSectionQueryFields() {
  return `
      canLoadMoreProducts
      isSpecialSection
      isPopularSection
      totalHits
      take
      tags {
        key
        value
        displayName
      }
      products {
        url
        productName
        brandName
        imageIds
        comingSoon
        articleNumber
        primaryImage
        secondaryImage
        primaryImageVersion
        secondaryImageVersion
        createdDate
        productFlags
        showLowImpactFlag
        colors
        hasMultipleSizes
      }`;
}
